import { useEventsCategoryId } from '@/hooks/useCatalog';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tag, Copy, Check, TicketPercent, Sparkles, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getPublicOffers, getBannerAdsPublic } from '../api/apiUtils';
import { EmptyState, Shimmer } from '@/components/ui';



const offerLabel = (o) => {
  if (o?.discountType === 'percentage') {
    const cap = o?.maxDiscountAmount ? ` up to ₹${o.maxDiscountAmount}` : '';
    return `${o.discountValue}% OFF${cap}`;
  }
  return `₹${o?.discountValue} OFF`;
};

const CouponCard = ({ offer, index }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!offer?.code) return;
    try {
      await navigator.clipboard.writeText(offer.code);
    } catch {
      /* clipboard may be blocked; still flash the confirmation */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div
      style={{ animationDelay: `${index * 50}ms` }}
      className="relative animate-floatUp overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card transition-shadow hover:shadow-cardHover"
    >
      {/* Left accent rail */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-brand-secondary to-blue-400" />
      <div className="p-5 pl-6">
        <div className="flex items-start justify-between gap-3">
          <div className="w-11 h-11 rounded-xl bg-brand-tint flex items-center justify-center flex-shrink-0">
            <TicketPercent className="w-6 h-6 text-brand-secondary" />
          </div>
          <span className="text-lg font-extrabold text-ink-900 text-right leading-tight">
            {offerLabel(offer)}
          </span>
        </div>

        <h3 className="mt-4 font-semibold text-ink-900">{offer?.name || 'Special offer'}</h3>
        {offer?.description && (
          <p className="text-sm text-ink-500 mt-1 line-clamp-2">{offer.description}</p>
        )}
        {offer?.minOrderAmount > 0 && (
          <p className="text-xs text-ink-500 mt-2">
            On orders above ₹{offer.minOrderAmount.toLocaleString('en-IN')}
          </p>
        )}

        {offer?.code && (
          <button
            type="button"
            onClick={copy}
            className="lk-focus group mt-4 flex min-h-[44px] w-full items-center justify-between rounded-xl border-2 border-dashed border-ink-200 px-4 py-2.5 transition-colors hover:border-brand-secondary"
          >
            <span className="font-mono font-bold tracking-wider text-ink-900">{offer.code}</span>
            <span className={`flex items-center gap-1 text-sm font-semibold ${copied ? 'text-success' : 'text-brand-secondary'}`}>
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

const Offers = () => {
  const eventsCategoryId = useEventsCategoryId();
  const [offers, setOffers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [offerRes, bannerRes] = await Promise.allSettled([
          getPublicOffers({ categoryId: eventsCategoryId, bookingAmount: 0 }),
          getBannerAdsPublic(),
        ]);

        if (!cancelled && offerRes.status === 'fulfilled') {
          const d = offerRes.value?.data || {};
          const merged = [...(d.applicableDiscounts || []), ...(d.nearApplicableDiscounts || [])];
          // Dedupe by code/id.
          const seen = new Set();
          const unique = merged.filter((o) => {
            const key = o?.code || o?.id;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setOffers(unique);
        }

        if (!cancelled && bannerRes.status === 'fulfilled') {
          const list = Array.isArray(bannerRes.value?.data)
            ? bannerRes.value.data
            : (bannerRes.value?.data?.data || []);
          setBanners(list.filter((b) => b?.imageUrl).slice(0, 3));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventsCategoryId]);

  const hasOffers = useMemo(() => offers.length > 0, [offers]);

  return (
    <div className="min-h-screen flex flex-col bg-surface-muted">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <div className="bg-gradient-to-br from-brand-secondary to-brand-blueDark text-white">
          <div className="lk-container py-10 sm:py-12">
            <div className="flex items-center gap-2 text-white/90 text-sm font-medium mb-2">
              <Sparkles className="w-4 h-4" /> Offers &amp; Coupons
            </div>
            <h1 className="text-h1">Save more on every booking</h1>
            <p className="text-white/80 mt-2 max-w-xl">
              Grab the latest coupons for events near you. Copy a code and apply it at checkout.
            </p>
          </div>
        </div>

        <div className="lk-container py-8 sm:py-10">
          {/* Promo banners */}
          {banners.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {banners.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => b?.bannerUrl && window.open(b.bannerUrl, '_blank', 'noopener,noreferrer')}
                  className="rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow h-36 relative text-left"
                >
                  <img src={b.imageUrl} alt={b.title || 'Offer'} className="w-full h-full object-cover" />
                  {b.title && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                      <span className="text-white font-semibold">{b.title}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mb-6">
            <Tag className="w-5 h-5 text-ink-700" />
            <h2 className="text-h2 text-ink-900">Coupons for you</h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Shimmer key={i} className="h-52 rounded-2xl" />
              ))}
            </div>
          ) : hasOffers ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {offers.map((o, i) => (
                <CouponCard key={o.code || o.id || i} offer={o} index={i} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={TicketPercent}
              title="No offers right now"
              message="Check back soon — new coupons drop regularly."
              action={
                <Link to="/" className="lk-btn-primary">
                  Browse events <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              }
            />
          )}

          {/* How to redeem */}
          <div className="lk-card mt-10 p-6 sm:p-8">
            <h2 className="text-h3 text-ink-900 mb-5">How to redeem</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { n: 1, t: 'Pick an event', d: 'Choose the event and ticket you want to book.' },
                { n: 2, t: 'Copy a code', d: 'Tap a coupon above to copy its code.' },
                { n: 3, t: 'Apply at checkout', d: 'Paste it in the coupon field before you pay.' },
              ].map((s) => (
                <div key={s.n} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-secondary text-white flex items-center justify-center font-bold flex-shrink-0">
                    {s.n}
                  </div>
                  <div>
                    <p className="font-semibold text-ink-900">{s.t}</p>
                    <p className="text-sm text-ink-500">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Offers;
